// Small controls for putting right, from an analysis view, a node a finding rests on: opening it in its model, or
// changing the one property behind the finding (how information is held, what kind a system is, the value of an
// output). They say what the user chose; the view saves it, with NodeCorrectionService
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NODE_TYPE, SYSTEM_KIND } from '../common/linqset-model';
import { SYSTEM_KIND_LABELS } from '../common/system-kind';

const FIX_STYLES = `
    :host { display: inline-flex; align-items: center; gap: 4px; vertical-align: middle; }
    select, input { font: inherit; font-size: 12px; padding: 2px 4px; border: 1px solid var(--line, #d5d9e3); border-radius: 6px; background: var(--paper, #fff); color: inherit; max-width: 100%; }
    select:focus-visible, input:focus-visible, button:focus-visible { outline: 2px solid var(--teal, #00a9a5); outline-offset: 1px; }
    button.link { padding: 0; border: none; background: none; color: var(--teal-dark, #00706d); font: inherit; font-size: 12px; cursor: pointer; text-decoration: underline; white-space: nowrap; }
    button.link:disabled { color: var(--grey, #9aa0b0); cursor: default; }
    .muted { color: var(--slate, #5b6172); font-size: 11px; }
`;

// a pencil beside a finding, opening the node it rests on in its model, with the node selected to edit
@Component({
    selector: 'app-edit-in-model',
    standalone: true,
    imports: [MatIconModule, MatTooltipModule],
    template: `
        <button type="button" class="edit" [matTooltip]="tooltip" [attr.aria-label]="tooltip" (click)="edit.emit(); $event.stopPropagation()" data-testid="edit-in-model">
            <mat-icon>edit</mat-icon>
        </button>
    `,
    styles: [`
        :host { display: inline-flex; vertical-align: middle; }
        .edit { display: inline-grid; place-items: center; width: 28px; height: 28px; padding: 0; border: 1px solid transparent; border-radius: 7px;
            background: transparent; color: var(--slate, #5b6172); cursor: pointer; transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease; }
        .edit:hover, .edit:focus-visible { background: var(--teal-soft, #e0f5f4); border-color: var(--teal, #00a9a5); color: var(--teal, #00a9a5); outline: none; }
        mat-icon { width: 18px; height: 18px; font-size: 18px; }
    `],
})
export class EditInModelComponent {
    @Input() name?: string;
    @Output() edit = new EventEmitter<void>();

    get tooltip() {
        return `Edit ${this.name || 'it'} in its model`;
    }
}

// how a piece of information is held is read from its type: these are the types that say so
export const HELD_AS_TYPES: { type: NODE_TYPE; label: string }[] = [
    { type: NODE_TYPE.DATABASE, label: 'In a system: database' },
    { type: NODE_TYPE.APPLICATION, label: 'In a system: application' },
    { type: NODE_TYPE.SERVICE, label: 'In a system: service' },
    { type: NODE_TYPE.FILE, label: 'As a file' },
    { type: NODE_TYPE.PAPER, label: 'On paper' },
    { type: NODE_TYPE.VERBAL, label: 'By word of mouth' },
    { type: NODE_TYPE.INFORMATION, label: 'Not said' },
];

// how a piece of information is held, to change; typed as plain information, LINQ's read of it, to confirm
@Component({
    selector: 'app-held-as-fix',
    standalone: true,
    imports: [FormsModule, MatTooltipModule],
    template: `
        <select [ngModel]="type" (ngModelChange)="choose($event)" [disabled]="disabled" [attr.aria-label]="'How ' + (name || 'it') + ' is held'" data-testid="held-as-fix">
            @for (o of options; track o.type) {<option [ngValue]="o.type">{{ o.type === NOT_SAID && readLabel ? "LINQ's read: " + readLabel : o.label }}</option>}
        </select>
        @if (readLabel && type === NOT_SAID) {
            <button type="button" class="link" (click)="changed.emit(read!)" [disabled]="disabled" matTooltip="LINQ read this from the systems attached to it and its name: confirm it" data-testid="held-as-confirm">Confirm</button>
        }
    `,
    styles: [FIX_STYLES],
})
export class HeldAsFixComponent implements OnChanges {
    @Input() type?: NODE_TYPE;
    @Input() name?: string;
    @Input() disabled = false;
    @Input() read?: NODE_TYPE;          // LINQ's read, where it's typed as plain information
    @Output() changed = new EventEmitter<NODE_TYPE>();
    options = HELD_AS_TYPES;
    readonly NOT_SAID = NODE_TYPE.INFORMATION;

    get readLabel() {
        return this.read !== undefined ? HELD_AS_TYPES.find(o => o.type === this.read)?.label : undefined;
    }

    ngOnChanges() {
        // a type that says nothing of how it is held is shown as not said
        if (this.type === undefined || !HELD_AS_TYPES.some(o => o.type === this.type)) this.type = NODE_TYPE.INFORMATION;
    }

    choose(type: NODE_TYPE) {
        if (type !== this.type) this.changed.emit(type);
    }
}

export interface SystemKindChoice { key: string; name: string; kind: SYSTEM_KIND; confirmed: boolean }

// the kind of each system a finding rests on, to confirm or change
@Component({
    selector: 'app-system-kind-fix',
    standalone: true,
    imports: [FormsModule, MatTooltipModule],
    template: `
        @for (s of systems; track s.key) {
            <label class="system">
                <span>{{ s.name }}</span>
                <select [ngModel]="s.kind" (ngModelChange)="choose(s, $event)" [disabled]="disabled" [attr.aria-label]="'What kind of system ' + s.name + ' is'" data-testid="system-kind-fix">
                    @for (k of kinds; track k) {<option [ngValue]="k">{{ labels[k] }}</option>}
                </select>
                @if (!s.confirmed) {
                    <button type="button" class="link" (click)="choose(s, s.kind, true)" [disabled]="disabled" matTooltip="LINQ read this from the system's name: confirm it">Confirm</button>
                }
            </label>
        }
    `,
    styles: [FIX_STYLES + `
        :host { flex-wrap: wrap; gap: 4px 12px; }
        .system { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; }
    `],
})
export class SystemKindFixComponent {
    @Input() systems: SystemKindChoice[] = [];
    @Input() disabled = false;
    @Output() changed = new EventEmitter<{ key: string; kind: SYSTEM_KIND }>();
    kinds = Object.keys(SYSTEM_KIND_LABELS) as SYSTEM_KIND[];
    labels = SYSTEM_KIND_LABELS;

    // a kind LINQ read is set when confirmed, as well as when changed
    choose(system: SystemKindChoice, kind: SYSTEM_KIND, confirm = false) {
        if (confirm || kind !== system.kind) this.changed.emit({ key: system.key, kind });
    }
}

// the value per item of an output, set from the view: a dead end that is worth something isn't friction
@Component({
    selector: 'app-value-fix',
    standalone: true,
    imports: [FormsModule],
    template: `
        @if (editing) {
            <input type="number" min="0" step="any" [(ngModel)]="value" [disabled]="disabled" [attr.aria-label]="'Value per item of ' + (name || 'the output')" (keydown.enter)="save()" (keydown.escape)="editing = false" data-testid="value-fix-input" />
            <button type="button" class="link" (click)="save()" [disabled]="disabled || !valid">Save</button>
            <button type="button" class="link" (click)="editing = false" [disabled]="disabled">Cancel</button>
        } @else {
            <button type="button" class="link" (click)="editing = true" [disabled]="disabled" data-testid="value-fix">{{ label }}</button>
        }
    `,
    styles: [FIX_STYLES + ` input { width: 80px; } `],
})
export class ValueFixComponent {
    @Input() name?: string;
    @Input() label = 'Set a value';
    @Input() disabled = false;
    @Output() set = new EventEmitter<number>();
    editing = false;
    value?: number;

    get valid() {
        return typeof this.value === 'number' && Number.isFinite(this.value) && this.value > 0;
    }

    save() {
        if (!this.valid) return;
        this.set.emit(this.value as number);
        this.editing = false;
    }
}
